import { useState } from 'react';
import { Pencil, Trash2, Unplug } from 'lucide-react';
import type {
  RoutingBreakerRecord,
  RoutingSkuTargetRecord,
  RoutingTargetDisplayNamesResponse,
  SmartOverviewTarget,
  SmartRole,
  TargetProbeLayer,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, IconButton, Input, Select, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import { BreakerStateChip } from '../RoutingBreakersDrawer.tsx';
import { breakerForTarget } from '../tenantRoutingShared.ts';
import type { LegSkuArming } from '../guaranteed/legModel.ts';
import type { TargetLegs } from './probeDot.ts';
import { ProbeStatusDot } from './ProbeStatusDot.tsx';
import { DisabledTargetChip } from './TenantRoutingSkuPoolRow.tsx';
import { TryProbeButton } from './TryProbeButton.tsx';
import { TenantRoutingTargetModel, TenantRoutingTargetSupply } from './TenantRoutingTargetSupply.tsx';
import {
  SMART_ROLES,
  formatMultiplier,
  isSmartRole,
  matchSmartOverviewTarget,
} from './smartRouting.ts';

const ROLE_LABEL: Record<SmartRole, I18nKey> = {
  self_pool: 'tenant_routing.smart.role.self_pool',
  external: 'tenant_routing.smart.role.external',
  reserve: 'tenant_routing.smart.role.reserve',
};

export interface SmartTargetFieldsPatch {
  readonly smart_role?: SmartRole | null;
  readonly smart_initial_score?: number | null;
}

function storedScore(target: RoutingSkuTargetRecord): string {
  return target.smart_initial_score === null || target.smart_initial_score === undefined
    ? ''
    : String(target.smart_initial_score);
}

function parseScore(raw: string): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 1) return { ok: false };
  return { ok: true, value };
}

function omitIdFlag(flags: Readonly<Record<string, true>>, id: string): Readonly<Record<string, true>> {
  return Object.fromEntries(Object.entries(flags).filter(([key]) => key !== id));
}

/**
 * Smart-mode target table. Role and initial score write through the
 * existing sku-targets PUT. Cost is read-only (procurement row).
 * Unpinned rows cannot PUT (capability_id required) and show a Chip.
 */
export function TenantRoutingSmartPoolTable({
  skuId,
  targets,
  displayNames,
  overviewTargets,
  overviewLoaded,
  breakers,
  deleting,
  resettingBreaker,
  savingSmartFields,
  onSaveSmartFields,
  onEdit,
  onDelete,
  onBreakerReset,
  onBreakerTrip,
  probeByTargetId,
  arming,
  probeLayer,
}: {
  readonly skuId: string;
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly displayNames?: RoutingTargetDisplayNamesResponse;
  readonly overviewTargets: readonly SmartOverviewTarget[];
  readonly overviewLoaded: boolean;
  readonly breakers: readonly RoutingBreakerRecord[];
  readonly deleting: boolean;
  readonly resettingBreaker: boolean;
  readonly savingSmartFields: boolean;
  readonly onSaveSmartFields: (target: RoutingSkuTargetRecord, patch: SmartTargetFieldsPatch) => void;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly onDelete: (target: RoutingSkuTargetRecord) => void;
  readonly onBreakerReset: (record: RoutingBreakerRecord) => void;
  readonly onBreakerTrip: (target: RoutingSkuTargetRecord) => void;
  readonly probeByTargetId: ReadonlyMap<string, TargetLegs>;
  readonly arming: LegSkuArming;
  /** Routing layer of this pool's rows, for the 试打 shot. */
  readonly probeLayer: TargetProbeLayer;
}): JSX.Element {
  const t = useT();
  const [scoreDraft, setScoreDraft] = useState<Readonly<Record<string, string>>>({});
  const [scoreError, setScoreError] = useState<Readonly<Record<string, true>>>({});

  return (
    <Table
      className="shrink-0 rounded-none border-0 bg-transparent"
      tableClassName="table-fixed min-w-[1194px]"
      testId={`tenant-routing-smart-pool-${skuId}`}
    >
      <THead>
        <Tr interactive={false}>
          <Th className="w-[290px]">{t('tenant_routing.supply.title')}</Th>
          <Th>{t('tenant_routing.supply.model')}</Th>
          <Th className="w-[116px]">{t('tenant_routing.smart.role')}</Th>
          <Th num className="w-[116px]">{t('tenant_routing.smart.cost_multiplier')}</Th>
          <Th num className="w-[96px]">{t('tenant_routing.smart.initial_score')}</Th>
          <Th className="w-[160px]">{t('tenant_routing.smart.enabled')}</Th>
          <Th className="w-[196px] text-right">{t('tenant_routing.table.actions')}</Th>
        </Tr>
      </THead>
      <TBody>
        {targets.map((target) => {
          const overview = matchSmartOverviewTarget(overviewTargets, target);
          const breaker = breakerForTarget(breakers, target);
          const cost = overview?.cost_multiplier ?? null;
          const noRuntime = overviewLoaded && overview === null;
          const missingCost = overviewLoaded && overview !== null && cost === null;
          const canWrite = target.capability_id !== null;
          const roleValue = target.smart_role !== undefined && target.smart_role !== null && isSmartRole(target.smart_role)
            ? target.smart_role
            : 'external';
          const scoreValue = scoreDraft[target.id] ?? storedScore(target);

          return (
            <Tr key={target.id} data-testid={`tenant-routing-smart-row-${target.id}`}>
              <Td className="align-top">
                <TenantRoutingTargetSupply target={target} names={displayNames} />
              </Td>
              <Td className="align-top">
                <TenantRoutingTargetModel target={target} names={displayNames} />
              </Td>
              <Td className="align-top">
                {canWrite ? (
                  <Select
                    value={roleValue}
                    onValueChange={(value) => {
                      if (!isSmartRole(value)) return;
                      onSaveSmartFields(target, { smart_role: value });
                    }}
                    options={SMART_ROLES.map((role) => ({ value: role, label: t(ROLE_LABEL[role]) }))}
                    disabled={savingSmartFields}
                    testId={`tenant-routing-smart-role-${target.id}`}
                  />
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`tenant-routing-smart-role-${target.id}`}>
                    <span>{t(ROLE_LABEL[roleValue])}</span>
                    <Chip tone="neutral" lamp={false} testId={`tenant-routing-smart-role-readonly-${target.id}`}>
                      {t('tenant_routing.smart.readonly')}
                    </Chip>
                  </span>
                )}
              </Td>
              <Td num className="align-top">
                <span className="inline-flex flex-col items-end gap-1">
                  {noRuntime ? (
                    <span
                      className="text-paper-low"
                      data-testid={`tenant-routing-smart-no-runtime-${target.id}`}
                    >
                      {t('tenant_routing.smart.no_runtime')}
                    </span>
                  ) : missingCost ? (
                    <span
                      className="text-bad"
                      data-testid={`tenant-routing-smart-missing-cost-${target.id}`}
                    >
                      {t('tenant_routing.smart.missing_cost')}
                    </span>
                  ) : (
                    <span className="font-mono text-paper">{formatMultiplier(cost)}</span>
                  )}
                  <Chip tone="neutral" lamp={false} testId={`tenant-routing-smart-cost-readonly-${target.id}`}>
                    {t('tenant_routing.smart.readonly')}
                  </Chip>
                </span>
              </Td>
              <Td num className="align-top">
                {canWrite ? (
                  <Input
                    mono
                    inputMode="decimal"
                    value={scoreValue}
                    invalid={scoreError[target.id] === true}
                    disabled={savingSmartFields}
                    onChange={(event) => {
                      const next = event.currentTarget.value;
                      setScoreDraft({ ...scoreDraft, [target.id]: next });
                      setScoreError(omitIdFlag(scoreError, target.id));
                    }}
                    onBlur={() => {
                      const parsed = parseScore(scoreDraft[target.id] ?? storedScore(target));
                      if (!parsed.ok) {
                        setScoreError({ ...scoreError, [target.id]: true });
                        return;
                      }
                      setScoreError(omitIdFlag(scoreError, target.id));
                      const current = target.smart_initial_score ?? null;
                      if (parsed.value === current) return;
                      onSaveSmartFields(target, { smart_initial_score: parsed.value });
                    }}
                    data-testid={`tenant-routing-smart-score-${target.id}`}
                  />
                ) : (
                  <span className="inline-flex flex-wrap items-center justify-end gap-1.5" data-testid={`tenant-routing-smart-score-${target.id}`}>
                    <span>{storedScore(target) === '' ? t('tenant_routing.smart.initial_score.empty') : storedScore(target)}</span>
                    <Chip tone="neutral" lamp={false} testId={`tenant-routing-smart-score-readonly-${target.id}`}>
                      {t('tenant_routing.smart.readonly')}
                    </Chip>
                  </span>
                )}
              </Td>
              <Td className="align-top">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <ProbeStatusDot targetId={target.id} legs={probeByTargetId.get(target.id) ?? null} arming={arming} />
                  {target.enabled ? (
                    <Chip tone="ok">{t('tenant_routing.status.target_enabled')}</Chip>
                  ) : (
                    <DisabledTargetChip target={target} onEdit={onEdit} />
                  )}
                  {breaker !== null ? <BreakerStateChip record={breaker} /> : null}
                </span>
              </Td>
              <Td className="align-top text-right">
                <span className="inline-flex items-center gap-1">
                  <TryProbeButton layer={probeLayer} targetId={target.id} />
                  {breaker !== null ? (
                    <Button
                      size="sm"
                      disabled={resettingBreaker}
                      onClick={() => onBreakerReset(breaker)}
                      data-testid={`tenant-routing-breaker-reset-${target.id}`}
                    >
                      {t('tenant_routing.breaker.reset')}
                    </Button>
                  ) : null}
                  {breaker === null || breaker.state !== 'forced' ? (
                    <IconButton
                      size="sm"
                      label={t('tenant_routing.breaker.trip')}
                      onClick={() => onBreakerTrip(target)}
                      data-testid={`tenant-routing-breaker-trip-${target.id}`}
                    >
                      <Unplug aria-hidden="true" className="h-3.5 w-3.5" />
                    </IconButton>
                  ) : null}
                  <IconButton
                    size="sm"
                    label={t('tenant_routing.action.edit_default')}
                    onClick={() => onEdit(target)}
                    data-testid={`tenant-routing-target-edit-${target.id}`}
                  >
                    <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton
                    size="sm"
                    label={t('tenant_routing.pool.remove_target')}
                    disabled={deleting}
                    onClick={() => onDelete(target)}
                    className="text-bad hover:text-bad"
                    data-testid={`tenant-routing-target-delete-${target.id}`}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                  </IconButton>
                </span>
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
}
